import { defineComponent, ref } from 'vue';
import Select, { selectProps } from '../select';
import PropTypes from '../_util/vue-types';
import { isValidElement, flattenChildren } from '../_util/props-util';
import warning from '../_util/warning';
import Option from './Option';
import OptGroup from './OptGroup';
import omit from '../_util/omit';
import useConfigInject from '../_util/hooks/useConfigInject';
function isSelectOptionOrSelectOptGroup(child) {
    var _a, _b;
    return ((_a = child === null || child === void 0 ? void 0 : child.type) === null || _a === void 0 ? void 0 : _a.isSelectOption) || ((_b = child === null || child === void 0 ? void 0 : child.type) === null || _b === void 0 ? void 0 : _b.isSelectOptGroup);
}
const autoCompleteProps = Object.assign(Object.assign({}, selectProps()), { dataSource: PropTypes.array, dropdownMenuStyle: PropTypes.style, optionLabelProp: PropTypes.string, dropdownMatchSelectWidth: { type: [Number, Boolean], default: true } });
export const AutoCompleteOption = Option;
export const AutoCompleteOptGroup = OptGroup;
const AutoComplete = defineComponent({
    name: 'AAutoComplete',
    inheritAttrs: false,
    props: Object.assign(Object.assign({}, autoCompleteProps), { prefixCls: PropTypes.string, showSearch: PropTypes.looseBool, transitionName: PropTypes.string.def('slide-up'), choiceTransitionName: PropTypes.string.def('zoom'), autofocus: PropTypes.looseBool, backfill: PropTypes.looseBool, optionLabelProp: PropTypes.string.def('children'), filterOption: PropTypes.oneOfType([PropTypes.looseBool, PropTypes.func]).def(false), defaultActiveFirstOption: PropTypes.looseBool.def(true) }),
    emits: ['change', 'select', 'focus', 'blur'],
    slots: ['option'],
    Option,
    OptGroup,
    setup(props, { slots, attrs, expose }) {
        warning(!('dataSource' in slots), 'AutoComplete', '`dataSource` slot is deprecated, please use props `options` instead.');
        warning(!('options' in slots), 'AutoComplete', '`options` slot is deprecated, please use props `options` instead.');
        const selectRef = ref();
        const getInputElement = () => {
            var _a;
            const children = flattenChildren((_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots));
            const element = children.length ? children[0] : undefined;
            return element;
        };
        const focus = () => {
            var _a;
            (_a = selectRef.value) === null || _a === void 0 ? void 0 : _a.focus();
        };
        const blur = () => {
            var _a;
            (_a = selectRef.value) === null || _a === void 0 ? void 0 : _a.blur();
        };
        expose({
            focus,
            blur,
        });
        const { prefixCls } = useConfigInject('select', props);
        return () => {
            var _a, _b, _c;
            const { size, dataSource, notFoundContent = (_a = slots.notFoundContent) === null || _a === void 0 ? void 0 : _a.call(slots) } = props;
            let optionChildren;
            const { class: className } = attrs;
            const cls = {
                [className]: !!className,
                [`${prefixCls.value}-lg`]: size === 'large',
                [`${prefixCls.value}-sm`]: size === 'small',
                [`${prefixCls.value}-show-search`]: true,
                [`${prefixCls.value}-auto-complete`]: true,
            };
            if (props.options === undefined) {
                const childArray = ((_b = slots.dataSource) === null || _b === void 0 ? void 0 : _b.call(slots)) || ((_c = slots.options) === null || _c === void 0 ? void 0 : _c.call(slots)) || [];
                if (childArray.length && isSelectOptionOrSelectOptGroup(childArray[0])) {
                    optionChildren = childArray;
                }
                else {
                    optionChildren = dataSource
                        ? dataSource.map((item) => {
                            if (isValidElement(item)) {
                                return item;
                            }
                            switch (typeof item) {
                                case 'string':
                                    return (<Option key={item} value={item}>
                        {item}
                      </Option>);
                                case 'object':
                                    return (<Option key={item.value} value={item.value}>
                        {item.text}
                      </Option>);
                                default:
                                    throw new Error('AutoComplete[dataSource] only supports type `string[] | Object[]`.');
                            }
                        })
                        : [];
                }
            }
            const selectProps = Object.assign(Object.assign(Object.assign({}, omit(props, ['dataSource', 'optionLabelProp'])), attrs), { mode: Select.SECRET_COMBOBOX_MODE_DO_NOT_USE, 
                // optionLabelProp,
                getInputElement,
                notFoundContent, 
                // placeholder: '',
                class: cls, ref: selectRef });
            return (<Select {...selectProps} v-slots={{ option: slots.option }}>
          {optionChildren}
        </Select>);
        };
    },
});
/* istanbul ignore next */
AutoComplete.install = function (app) {
    app.component(AutoComplete.name, AutoComplete);
    app.component(AutoComplete.Option.displayName, AutoComplete.Option);
    app.component(AutoComplete.OptGroup.displayName, AutoComplete.OptGroup);
    return app;
};
export default AutoComplete;
